import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AiAssetGeneratorModal from "../../create_training_content/AiAssetGeneratorModal";
import {
  generateTimeOptionsDF,
  PurpleSwitch,
} from "../../../../helpers/helpers";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import StoreScopeTree from "./StoreScopeTree";
import SubtaskDrawer from "./SubtaskDrawer";
import { AUDIENCE_PERMISSION_OPTIONS, DEFAULT_GRADE_BANDS, sortBands } from "../tasks_hub/taskHubUtils";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { uploadFile } from "../../requests/utilities/requests";
import CloseIcon from "@mui/icons-material/Close";
import { Lightbox } from "react-modal-image";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { formatDate } from "../../requests/utilities/helpers";
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";

const TASK_TYPES = {
  ONE_OFF: "one_off",
  REPEATING: "repeating",
};

// Cadence is the recurrence. daily uses days_of_week (any subset of days),
// weekly is any time during the business week, monthly any time during the
// month, one_off uses due_at.
const CADENCES = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "one_off", label: "One-off" },
];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

const cadenceOf = (data) => {
  if (data?.cadence) return data.cadence;
  if (data?.type === TASK_TYPES.ONE_OFF) return "one_off";
  const days = data?.days_of_week || [];
  if (data?.repeating_period === "monthly") return "monthly";
  return days.length && days.length < 7 ? "weekly" : "daily";
};

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

// Grade bands for a scored audit: a label and the minimum percent that earns
// it, highest first. The lowest band always starts at 0 so every score gets a
// grade; the backend validates and re-sorts on save.
// The form's field recipe (see the Name field): 40px, white, rounded, and
// the violet focus border instead of MUI's default blue.
const bandInputProps = {
  sx: {
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "5px",
    height: "40px",
    fontSize: "14px",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
    },
  },
};

function GradeBandEditor({ bands, onChange }) {
  const rows = bands.length ? bands : DEFAULT_GRADE_BANDS.map((b) => ({ ...b }));
  // The floor is the lowest NUMERIC threshold; a row blanked to be retyped
  // is never the floor, or it would lock itself the moment it was cleared.
  const numeric = rows
    .map((b) => (b.min_percent === "" || b.min_percent == null ? NaN : Number(b.min_percent)))
    .filter((n) => Number.isFinite(n));
  const lowest = numeric.length ? Math.min(...numeric) : null;
  const update = (index, patch) =>
    onChange(rows.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const add = () => {
    const sorted = sortBands(rows);
    const top = sorted.length ? sorted[0].min_percent : 0;
    const next = Math.min(100, Math.round(top + (100 - top) / 2));
    onChange([{ label: "", min_percent: next }, ...rows]);
  };
  return (
    <div className="mt-3 border border-slate-200 rounded-xl bg-white px-4 py-3">
      <div className="grid grid-cols-[1fr_120px_32px] gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
        <span>Grade</span>
        <span>From %</span>
        <span />
      </div>
      {rows.map((b, i) => {
        const isFloor = lowest != null && b.min_percent !== "" && b.min_percent != null && Number(b.min_percent) === lowest;

        return (
          <div key={i} className="grid grid-cols-[1fr_120px_32px] gap-2 items-center py-1">
            <TextField
              placeholder={i === 0 ? "A" : "Grade"}
              variant="outlined"
              size="small"
              value={b.label ?? ""}
              onChange={(e) => update(i, { label: e.target.value })}
              InputProps={bandInputProps}
            />
            <TextField
              type="number"
              variant="outlined"
              size="small"
              value={b.min_percent ?? ""}
              disabled={isFloor && rows.length > 1}
              onChange={(e) => update(i, { min_percent: e.target.value === "" ? "" : Number(e.target.value) })}
              inputProps={{ min: 0, max: 100, step: 1 }}
              InputProps={bandInputProps}
            />

            {isFloor ? (
              <span className="text-[10px] text-slate-400 text-center">floor</span>
            ) : (
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label="Remove grade band"
                className="w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-rose-300 hover:text-rose-600 flex items-center justify-center transition-colors"
              >
                <CloseIcon style={{ fontSize: "14px" }} />
              </button>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        className="mt-1 text-xs font-semibold text-violet-700 hover:text-violet-800"
      >
        + Add a grade band
      </button>
    </div>
  );
}


// A deadline is an instant, and the picker shows a wall clock. Both have to be
// read in the STORE's zone: 5 PM chosen by a manager in Eastern time for a
// Pacific store means 5 PM there, not 2 PM.
const zonedPicker = (iso, tz) =>
  iso ? (tz ? dayjs(iso).tz(tz) : dayjs(iso)) : null;
const zonedToIso = (value, tz) => {
  if (!value || !dayjs(value).isValid()) return null;
  if (!tz) return dayjs(value).toISOString();
  return dayjs.tz(dayjs(value).format("YYYY-MM-DDTHH:mm:ss"), tz).toISOString();
};

function DailyPlanFormBuilder(props) {
  // Stores the picker offers: the company-wide store groups payload when the
  // hub fetched it (every open store, with its region or district), else the
  // caller's own memberships, which is all the form ever had before.
  const companyStores = (props.storeNames || []).filter(
    (s) => s.company === props.homeCompany
  );
  // A LOADED payload, even one with no open stores (every location closed),
  // is the authority; the caller's own memberships are only the fallback for
  // a payload that could not be fetched.
  const scopeStoresBase = Array.isArray(props.storeGroups?.stores)
    ? props.storeGroups.stores
    : companyStores.map((s) => ({ id: s.id, store_name: s.store_name, store_group_uuid: null }));
  // The groups payload lists OPEN stores only. A store this task targets
  // that has since closed is still a target (it resumes there if the
  // location reopens), so it stays visible and named: picked on its own,
  // removable; reached through a picked group, placed under that group so
  // it follows the pick (unticking the group frees it like any other store).
  // A backend that does not say which pick it came from marks it inherited
  // outright.
  const knownStoreIds = new Set(scopeStoresBase.map((s) => s.id));
  const vias = props.modalData?.location_specific_store_vias || {};
  const closedRow = (id, derived) => {
    const known = (props.storeNames || []).find((s) => s.id === id);
    const via = derived ? vias[String(id)] || null : null;
    return {
      id,
      store_name: `${known?.store_name || `Location ${id}`} (closed)`,
      store_group_uuid: via,
      inherited: derived && !via,
    };
  };
  const closedPicks = (props.modalData?.location_specific_stores || [])
    .filter((id) => !knownStoreIds.has(id))
    .map((id) => closedRow(id, false));
  const closedDerived = (props.modalData?.location_specific_derived_stores || [])
    .filter((id) => !knownStoreIds.has(id))
    .map((id) => closedRow(id, true));
  // A closed member a one-off leaves out has no row, but the editor still
  // needs to see why the picked group omits it; it sits under the pick that
  // reaches it, so once the exclusion goes stale (a schedule edit) it
  // inherits the pick again like any other member.
  const excludedVias = props.modalData?.location_specific_excluded_vias || {};
  const closedExcluded = (props.modalData?.location_specific_excluded_stores || [])
    .filter((id) => !knownStoreIds.has(id))
    .map((id) => ({ ...closedRow(id, false), store_group_uuid: excludedVias[String(id)] || null, inherited: false }));
  const scopeStores = closedPicks.length || closedDerived.length || closedExcluded.length
    ? [...scopeStoresBase, ...closedPicks, ...closedDerived, ...closedExcluded]
    : scopeStoresBase;
  const scopeGroups = props.storeGroups?.groups || [];
  // The payload is null only when it could not be fetched (a company without
  // groups still gets one). A task that targets regions or districts cannot
  // be shown honestly without it: the picker would draw an empty scope while
  // a save kept the hidden picks. Such a task is held until it loads.
  const scopeUnavailable =
    props.storeGroups == null && (props.modalData?.location_specific_store_groups || []).length > 0;
  // Stores a one-off leaves out (opens there after its deadline), as the
  // backend derived them under the schedule the task was loaded with. Once
  // the deadline, start time or date is edited they are stale and the
  // backend will re-derive on save, so the picker stops showing them.
  const excludedFor = props.modalData?.location_specific_excluded_for;
  const scheduleUnchanged =
    !excludedFor ||
    ((props.modalData?.due_at ?? null) === excludedFor.due_at &&
      (props.modalData?.lower_bound_qhnum ?? null) === excludedFor.lower_bound_qhnum &&
      (props.modalData?.one_off_date ?? null) === excludedFor.one_off_date);
  const excludedStoreIds = scheduleUnchanged ? props.modalData?.location_specific_excluded_stores || [] : [];

  const [open, setOpen] = useState(false);
  const [showLighbox, setShowLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(
    props.distinctCategories?.length === 0
  );
  const [timeSelectionChecked, setTimeSelectionChecked] = useState(
    props.modalData?.lower_bound_qhnum !== null &&
      props.modalData?.upper_bound_qhnum !== null &&
      !(
        props.modalData?.lower_bound_qhnum === 0 &&
        props.modalData?.upper_bound_qhnum === 0
      )
  );
  const [intradayChecked, setIntradayChecked] = useState(
    props.modalData?.intraday_repeat_interval_minutes != null
  );

  const cadence = cadenceOf(props.modalData);
  const isRoutine = cadence !== "one_off";
  const scheduleDays = props.modalData?.days_of_week ?? ALL_DAYS;
  const [scheduleError, setScheduleError] = useState(null);
  // The validation message lives in the Schedule section, well below the
  // Save button at the top of a long form; a refused save also says so
  // where the click happened and brings the message into view.
  const scheduleErrorRef = React.useRef(null);

  // The payload the hub posts. Only the fields the cadence uses are sent so a
  // stale value from a previous cadence never rides along.
  const buildPayload = (plan_task_uuid) => ({
    plan_task_uuid,
    name: props.modalData?.name,
    description: props.modalData?.description,
    category: props.modalData?.category,
    cadence,
    lower_bound_qhnum: isRoutine ? props.modalData?.lower_bound_qhnum : null,
    upper_bound_qhnum: isRoutine ? props.modalData?.upper_bound_qhnum : null,
    requires_validation: !!props.modalData?.requires_validation,
    track_time: !!props.modalData?.track_time,
    scoring_enabled: !!props.modalData?.scoring_enabled,
    grade_bands: props.modalData?.scoring_enabled ? props.modalData?.grade_bands || [] : [],
    audience_permission_types: props.modalData?.audience_permission_types || [],
    subtasks: props.modalData?.subtasks,
    file_uploads:
      props.modalData?.file_uploads?.length > 0
        ? props.modalData?.file_uploads?.map((v) => v.uuid)
        : [],
    auto_assignable_roles: props.modalData?.auto_assignable_roles,
    is_location_specific: props.modalData?.is_location_specific,
    // A manager may edit a region- or district-targeted task (when they
    // manage every store it expands to) but may not pick groups, so their
    // copy (no uuid) carries today's expansion as explicit stores instead of
    // the picks, which the backend would reject on a create.
    ...(!plan_task_uuid && isManager && (props.modalData?.location_specific_store_groups || []).length > 0
      ? {
          location_specific_stores: [
            ...new Set([
              ...(props.modalData?.location_specific_derived_stores || []),
              ...(props.modalData?.location_specific_stores || []),
            ]),
          ],
          location_specific_store_groups: [],
        }
      : {
          location_specific_stores: props.modalData?.location_specific_stores,
          location_specific_store_groups: props.modalData?.location_specific_store_groups || [],
        }),
    intraday_repeat_interval_minutes:
      cadence === "daily" ? props.modalData?.intraday_repeat_interval_minutes : null,
    days_of_week: cadence === "daily" ? scheduleDays : undefined,
    day_of_month: cadence === "monthly" ? props.modalData?.day_of_month : undefined,
    due_at: cadence === "one_off" ? props.modalData?.due_at : undefined,
    one_off_date: cadence === "one_off" ? props.modalData?.one_off_date || null : undefined,
  });

  const validateSchedule = () => {
    if (cadence === "one_off" && !props.modalData?.due_at) {
      return "Pick when this one-off task is due.";
    }
    if (props.modalData?.scoring_enabled) {
      const bands = props.modalData?.grade_bands || [];
      if (bands.some((b) => !String(b.label || "").trim())) {
        return "Every grade band needs a label.";
      }
      if (bands.some((b) => !Number.isFinite(Number(b.min_percent)) || Number(b.min_percent) < 0 || Number(b.min_percent) > 100)) {
        return "Grade band minimums must be between 0 and 100.";
      }
      const mins = bands.map((b) => Number(b.min_percent));
      if (new Set(mins).size !== mins.length) {
        return "Two grade bands start at the same percent.";
      }
      const labels = bands.map((b) => String(b.label || "").trim().toLowerCase());
      if (new Set(labels).size !== labels.length) {
        return "Two grade bands have the same name.";
      }

      if (bands.length && Math.min(...mins) !== 0) {
        return "The lowest grade band must start at 0.";
      }
      if (!(props.modalData?.subtasks || []).some((s) => s.type === "pass_fail" && !s.is_optional)) {
        return "A scored audit needs at least one required Pass / Fail item.";
      }

      if (cadence === "daily" && props.modalData?.intraday_repeat_interval_minutes) {
        return "A scored audit can't repeat during the day. Turn off the repeat or the scoring.";
      }
      if (
        !(props.modalData?.audience_permission_types || []).length &&
        !(props.modalData?.auto_assignable_roles || []).length
      ) {
        return "Choose who conducts this audit: company admins, store managers, or specific roles.";
      }

    }
    if (cadence === "daily" && scheduleDays.length === 0) {
      return "Pick at least one day.";
    }
    return null;
  };

  const submit = (plan_task_uuid) => {
    const error = locationSpecificChecked && scopeUnavailable
      ? "This task's regions and districts could not be loaded. Try again before saving."
      : validateSchedule();
    setScheduleError(error);
    if (error) {
      toast.dismiss();
      toast.error(error, { autoClose: 5000 });
      setTimeout(() => {
        scheduleErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    props.post_plan_task(buildPayload(plan_task_uuid));
    props.handleCloseFormBuilder();
  };

  const default_subtask_data = {
    uuid: null,
    name: "",
    description: "",
    type: "text",
    is_image_required: false,
    is_optional: true,
  };

  const [subtaskData, setSubtaskData] = useState(default_subtask_data);
  const [subtaskEdit, setSubtaskEdit] = useState(false);
  const [editingSubtaskIndex, setEditingSubtaskIndex] = useState(-1);
  const [aiGenOpen, setAiGenOpen] = useState(false);

  // Seed the form from an AI draft. Role NAMES come back from the backend
  // (validated against the company's role list) and map to role ids here;
  // location scoping stays with the author. The saved task lands as a draft
  // regardless, so nothing generated can surface to employees unreviewed.
  const applyAiDraft = (draft) => {
    const roleIdByName = new Map();
    (props.roleNames || []).forEach((r) => {
      if (r?.id != null && r?.role_name) {
        roleIdByName.set(String(r.role_name).toLowerCase(), r.id);
      }
    });
    const roleIds = [];
    (draft.roles || []).forEach((n) => {
      const id = roleIdByName.get(String(n || "").toLowerCase());
      if (id != null && !roleIds.includes(id)) roleIds.push(id);
    });

    const hasWindow =
      draft.lower_bound_qhnum != null && draft.upper_bound_qhnum != null;
    props.setModalData((prevData) => ({
      ...prevData,
      name: draft.name || "",
      description: draft.description || "",
      category: draft.category || "",
      type: draft.type === TASK_TYPES.ONE_OFF ? TASK_TYPES.ONE_OFF : TASK_TYPES.REPEATING,
      // The generator names the cadence. Particular days are Daily with a
      // mask; weekly / monthly are any time during the period (no days).
      cadence:
        draft.type === TASK_TYPES.ONE_OFF
          ? "one_off"
          : ["daily", "weekly", "monthly"].includes(draft.cadence)
          ? draft.cadence
          : "daily",
      one_off_date: draft.type === TASK_TYPES.ONE_OFF ? draft.one_off_date : null,
      due_at:
        draft.type === TASK_TYPES.ONE_OFF && draft.one_off_date
          ? zonedToIso(`${draft.one_off_date}T17:00:00`, props.storeTz)
          : null,
      day_of_month: null,
      days_of_week:
        draft.type !== TASK_TYPES.ONE_OFF && (!draft.cadence || draft.cadence === "daily")
          ? draft.days_of_week?.length
            ? draft.days_of_week
            : [0, 1, 2, 3, 4, 5, 6]
          : [],
      lower_bound_qhnum: hasWindow ? draft.lower_bound_qhnum : null,
      upper_bound_qhnum: hasWindow ? draft.upper_bound_qhnum : null,
      intraday_repeat_interval_minutes: hasWindow
        ? draft.intraday_repeat_interval_minutes ?? null
        : null,
      auto_assignable_roles: roleIds,
      subtasks: (draft.subtasks || []).map((s) => ({
        uuid: null,
        name: s.name || "",
        description: s.description || "",
        type: s.type || "checkbox",
        is_image_required: !!s.is_image_required,
        is_optional: !!s.is_optional,
      })),
    }));
    setTimeSelectionChecked(hasWindow);
    setIntradayChecked(hasWindow && draft.intraday_repeat_interval_minutes != null);
    // A generated category that isn't an existing one flips the category
    // input into create mode so the value shows (and saves) as typed.
    if (draft.category && !(props.distinctCategories || []).includes(draft.category)) {
      setShowCreateCategory(true);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSaveSubtask = (newSubtaskData) => {
    if (subtaskEdit) {
      // Edit existing subtask
      const updatedSubtasks = [...props.modalData?.subtasks];
      updatedSubtasks[editingSubtaskIndex] = newSubtaskData;
      props.setModalData((prevData) => ({
        ...prevData,
        subtasks: updatedSubtasks,
      }));
    } else {
      // Add new subtask
      props.setModalData((prevData) => ({
        ...prevData,
        subtasks: [...prevData?.subtasks, newSubtaskData],
      }));
    }
    handleClose();
  };

  const handleEditSubtask = (index) => {
    setSubtaskEdit(true);
    setSubtaskData(props.modalData?.subtasks[index]);
    setEditingSubtaskIndex(index);
    handleOpen();
  };

  const handleDeleteSubtask = (index) => {
    props.setModalData((prevData) => ({
      ...prevData,
      subtasks: prevData?.subtasks?.filter((_, i) => i !== index),
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    props.setModalData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDueChange = (value) => {
    const iso = zonedToIso(value, props.storeTz);
    // Only the deadline. "Available from" is optional and has its own control;
    // left blank, the backend derives the business date the deadline falls on,
    // which a calendar date would get wrong for an after-midnight due time.
    props.setModalData((prevData) => ({
      ...prevData,
      due_at: iso,
    }));
  };

  const handleAvailableFromChange = (date) => {
    props.setModalData((prevData) => ({
      ...prevData,
      one_off_date: date && dayjs(date).isValid() ? formatDate(date) : null,
    }));
  };

  const handleCadenceChange = (event) => {
    const { value } = event.target;
    setScheduleError(null);
    props.setModalData((prevData) => {
      const next = {
        ...prevData,
        cadence: value,
        type: value === "one_off" ? TASK_TYPES.ONE_OFF : TASK_TYPES.REPEATING,
      };
      if (value === "daily") {
        next.days_of_week = ALL_DAYS;
        next.day_of_month = null;
      } else if (value === "weekly" || value === "monthly") {
        // Any time during the week / month: no days to pick.
        next.days_of_week = [];
        next.day_of_month = null;
        next.intraday_repeat_interval_minutes = null;
      } else {
        next.days_of_week = [];
        next.day_of_month = null;
        next.intraday_repeat_interval_minutes = null;
        next.lower_bound_qhnum = null;
        next.upper_bound_qhnum = null;
      }
      return next;
    });
    if (value !== "daily") setIntradayChecked(false);
    if (value === "one_off") setTimeSelectionChecked(false);
  };

  const handleTimeChange = (event) => {
    const { name, value } = event.target;
    props.setModalData((prevData) => ({
      ...prevData,
      [name === "start_time" ? "lower_bound_qhnum" : "upper_bound_qhnum"]:
        value,
    }));
  };

  const handleCategoryChange = (event) => {
    const { value } = event.target;
    props.setModalData((prevData) => ({
      ...prevData,
      category: value,
    }));
  };

  const handleDeleteFile = (fileIndex) => {
    props.setModalData((prevData) => ({
      ...prevData,
      file_uploads: prevData.file_uploads.filter(
        (_, index) => index !== fileIndex
      ),
    }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsFileLoading(true);
      try {
        const fileObject = await uploadFile(
          props.homeCompany,
          file,
          "plan_task"
        );

        setIsFileLoading(false);

        props.setModalData((prevData) => ({
          ...prevData,
          file_uploads: [...prevData?.file_uploads, fileObject],
        }));
      } catch (error) {
        console.error("Failed to upload file:", error);
        setIsFileLoading(false);
      }
    }
  };

  // Admin-ness is COMPANY-scoped (any admin/internal membership in the
  // company), while permissionRole only reflects the store being viewed. The
  // hub passes the company-scoped flag as isAdminUser; prefer it so an admin
  // visiting a store where they are merely a manager is not forced into
  // location-specific tasks (which would break editing company-wide ones).
  // Legacy callers that omit the prop fall back to the raw store role.
  const isManager =
    props.isAdminUser != null
      ? !props.isAdminUser
      : props.permissionRole === "company_manager";

  const [locationSpecificChecked, setLocationSpecificChecked] = useState(
    isManager ? true : (props.modalData?.is_location_specific || false)
  );

  // Force is_location_specific for managers
  React.useEffect(() => {
    if (isManager && !props.modalData?.is_location_specific) {
      props.setModalData((prevData) => ({
        ...prevData,
        is_location_specific: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  const handleLocationSpecificChange = (event) => {
    const isChecked = event.target.checked;
    setLocationSpecificChecked(isChecked);

    props.setModalData((prevData) => ({
      ...prevData,
      is_location_specific: isChecked,
      location_specific_stores: isChecked ? (prevData.location_specific_stores || []) : [],
      location_specific_store_groups: isChecked ? (prevData.location_specific_store_groups || []) : [],
    }));
  };

  const handleScopeChange = ({ groupUuids, storeIds }) => {
    props.setModalData((prevData) => ({
      ...prevData,
      location_specific_store_groups: groupUuids,
      location_specific_stores: storeIds,
    }));
  };

  const handleOpenPdfInNewTab = (fileUrl) => {
    window.open(fileUrl, "_blank");
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const reorderedSubtasks = Array.from(props.modalData.subtasks);
    const [reorderedItem] = reorderedSubtasks.splice(result.source.index, 1);
    reorderedSubtasks.splice(result.destination.index, 0, reorderedItem);

    props.setModalData((prevData) => ({
      ...prevData,
      subtasks: reorderedSubtasks,
    }));
  };

  return (
    <>
      {showLighbox && (
        <div className="absolute w-screen h-screen z-40">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="pb-10">
          <div className="sticky top-0 z-10 bg-white pt-4 pb-3 border-b border-slate-100">
            <div className="w-full flex flex-col items-center">
              <div className="w-[65%] flex items-center justify-between">
                <div
                  className="text-slate-500 hover:text-slate-600 text-sm flex items-center cursor-pointer"
                  onClick={() => props.handleCloseFormBuilder()}
                >
                  <div>
                    <ChevronLeftIcon
                      style={{ fontSize: "18px", marginBottom: "1px" }}
                    />
                  </div>
                  <p>Back to Task List</p>
                </div>
                <div className="flex items-center space-x-2">
                  {!props.modalEdit && (
                    <div
                      className="px-4 py-2 text-sm font-semibold bg-white border border-violet-200 text-violet-700 hover:bg-violet-50 rounded-lg cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                      onClick={() => setAiGenOpen(true)}
                    >
                      <AutoAwesomeIcon style={{ fontSize: 16 }} />
                      Generate with AI
                    </div>
                  )}
                  {props.modalEdit ? (
                    <div
                      className="px-4 py-2 text-sm font-semibold bg-white border border-slate-200 text-rose-600 hover:border-rose-300 rounded-lg cursor-pointer whitespace-nowrap"
                      onClick={() => {
                        props.delete_plan_task(props.modalData?.uuid);
                        props.handleCloseFormBuilder();
                      }}
                    >
                      Delete
                    </div>
                  ) : (
                    <div
                      className="px-4 py-2 text-sm font-semibold bg-white border border-slate-200 text-slate-600 hover:border-slate-400 rounded-lg cursor-pointer whitespace-nowrap"
                      onClick={() => props.handleCloseFormBuilder()}
                    >
                      Cancel
                    </div>
                  )}
                  {props.modalEdit && (
                    <div
                      className="px-4 py-2 text-sm font-semibold bg-white border border-slate-200 text-slate-600 hover:border-slate-400 rounded-lg cursor-pointer whitespace-nowrap"
                      onClick={() => submit(null)}
                    >
                      Make Copy
                    </div>
                  )}
                  <div
                    className="px-4 py-2 text-sm font-semibold bg-violet-700 hover:bg-violet-800 text-white rounded-lg shadow-sm cursor-pointer whitespace-nowrap"
                    onClick={() => submit(props.modalData?.uuid || null)}
                  >
                    Save
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col items-center">
            <h2 className="w-[65%] text-xl font-semibold text-slate-900 mt-6">
              {props.modalEdit ? "Edit Task" : "Create Task"}
            </h2>

            <div className="p-6 border border-slate-200 rounded-xl w-[65%] mt-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5">Basics</h3>
              <div>
                <p className="text-sm text-slate-500">Name</p>
                <div className="mt-1">
                  <TextField
                    placeholder="FOH Opening Subtask List"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="name"
                    value={props.modalData?.name}
                    onChange={handleInputChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "40px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>

              <div className="mt-5">
                <p className="text-sm text-slate-500">Description</p>
                <div className="mt-1">
                  <TextField
                    placeholder="Please complete this subtask list before the store opens"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="description"
                    value={props.modalData?.description}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "12px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center space-x-4">
                  <p className="text-sm text-slate-500">Category</p>
                  {showCreateCategory ? (
                    <p
                      className="text-xs text-violet-700 hover:text-violet-800 cursor-pointer"
                      onClick={() =>
                        setShowCreateCategory((prevState) => !prevState)
                      }
                    >
                      Back to Options
                    </p>
                  ) : (
                    <p
                      className="text-xs text-violet-700 hover:text-violet-800 cursor-pointer"
                      onClick={() => {
                        setShowCreateCategory((prevState) => !prevState);
                        if (!showCreateCategory) {
                          props.setModalData((prevData) => ({
                            ...prevData,
                            category: "",
                          }));
                        }
                      }}
                    >
                      Create New
                    </p>
                  )}
                </div>
                {showCreateCategory ? (
                  <div className="mt-1">
                    <TextField
                      placeholder="Create a new category"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="category"
                      value={props.modalData?.category}
                      onChange={handleInputChange}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "40px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  </div>
                ) : (
                  <div className="mt-1">
                    <Select
                      fullWidth
                      value={props.modalData?.category}
                      name="category"
                      onChange={handleCategoryChange}
                      size="small"
                      placeholder="General"
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "40px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      }}
                    >
                      {props.distinctCategories?.map((category, index) => (
                        <MenuItem key={index} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                )}
              </div>

              <div className="border-b border-slate-200 my-8"></div>

              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5">Resources</h3>
              <div>
                <div>
                  <p className="text-sm text-slate-500">Attachments</p>
                  {props.modalData?.file_uploads?.map((file, index) => {
                    const fileType = file.name.split(".").pop();
                    return (
                      <div
                        key={index}
                        className="py-1 flex items-center space-x-2"
                      >
                        <div
                          className="text-slate-500 hover:text-slate-900 cursor-pointer"
                          onClick={() => handleDeleteFile(index)}
                        >
                          <CloseIcon
                            style={{ fontSize: "18px", marginBottom: "2px" }}
                          />
                        </div>
                        {fileType === "pdf" ? (
                          <div
                            onClick={() => {
                              handleOpenPdfInNewTab(file.presigned_url);
                            }}
                          >
                            <p className="text-sm text-violet-700 hover:text-violet-800 cursor-pointer">
                              {file.name}
                            </p>
                          </div>
                        ) : (
                          <div
                            onClick={() => {
                              setSelectedImage(file.presigned_url);
                              setShowLightbox(true);
                            }}
                          >
                            <p className="text-sm text-violet-700 hover:text-violet-800 cursor-pointer">
                              {file.name}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="mt-2">
                    <label
                      htmlFor={`file-upload-plan`}
                      className="text-slate-500 hover:text-slate-900 flex items-center space-x-1 cursor-pointer"
                    >
                      <div>
                        {!isFileLoading ? (
                          <AttachFileIcon
                            style={{ fontSize: "20px", marginBottom: "2px" }}
                          />
                        ) : (
                          <CircularProgress
                            size="1rem"
                            sx={{
                              color: "#6d28d9",
                              marginTop: "4px",
                              marginRight: "4px",
                            }}
                          />
                        )}
                      </div>
                      <p className="text-sm">Attach resource</p>
                    </label>
                    <input
                      id={`file-upload-plan`}
                      name={`file-upload-plan`}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleFileChange(e)}
                      accept=".jpg, .jpeg, .png, .pdf"
                    />
                  </div>
                </div>
              </div>

              <div className="border-b border-slate-200 my-8"></div>

              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5">Subtasks</h3>
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    The checklist employees complete, in order.
                  </p>
                  <button
                    className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg px-3 py-1.5 whitespace-nowrap"
                    onClick={() => {
                      setSubtaskEdit(false);
                      setSubtaskData(default_subtask_data);
                      handleOpen();
                    }}
                  >
                    <AddIcon style={{ fontSize: "16px" }} />
                    Add subtask
                  </button>
                </div>
                {props.modalData?.subtasks?.length > 0 ? (
                  <div className="mt-3">
                    <Droppable droppableId="subtasks">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="w-full border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm"
                        >
                          {props.modalData?.subtasks?.map((subtask, index) => (
                            <Draggable
                              key={index}
                              draggableId={`subtask-${index}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`py-3 px-4 flex items-center gap-3 bg-white ${
                                    index !==
                                      props.modalData?.subtasks?.length - 1 &&
                                    !snapshot.isDragging
                                      ? "border-b border-slate-100"
                                      : ""
                                  } ${
                                    snapshot.isDragging
                                      ? "bg-slate-50 rounded-lg shadow-md ring-1 ring-slate-200"
                                      : ""
                                  }`}
                                >
                                  <div
                                    className="text-slate-300 hover:text-slate-500 flex-none"
                                    {...provided.dragHandleProps}
                                  >
                                    <DragIndicatorIcon style={{ fontSize: "20px" }} />
                                  </div>
                                  {/* Type marker, matching the drawer/employee
                                      checklist language: box = checkbox,
                                      Aa = text answer; headings are typography. */}
                                  {subtask?.type === "text" ? (
                                    <span className="w-5 h-5 rounded-md flex-none flex items-center justify-center text-[9px] font-extrabold border-2 border-slate-300 text-slate-400">
                                      Aa
                                    </span>
                                  ) : subtask?.type === "pass_fail" ? (
                                    <span className="w-5 h-5 rounded-md flex-none flex items-center justify-center text-[8px] font-extrabold border-2 border-slate-300 text-slate-400 tracking-tighter">
                                      P/F
                                    </span>
                                  ) : subtask?.type !== "heading" ? (
                                    <span className="w-5 h-5 rounded-md flex-none border-2 border-slate-300" />
                                  ) : null}
                                  <div className="flex-1 min-w-0">
                                    {subtask?.type === "heading" ? (
                                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 truncate">
                                        {subtask?.name}
                                      </p>
                                    ) : (
                                      <p className="text-sm font-medium text-slate-800 truncate">
                                        {subtask?.name}
                                      </p>
                                    )}
                                    {subtask?.description && (
                                      <p className="text-xs text-slate-400 truncate">
                                        {subtask?.description}
                                      </p>
                                    )}
                                  </div>
                                  {subtask?.type === "heading" && (
                                    <span className="inline-flex items-center text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 flex-none">
                                      Heading
                                    </span>
                                  )}
                                  {subtask?.type !== "heading" &&
                                    subtask?.is_image_required && (
                                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 flex-none">
                                        <CameraAltIcon style={{ fontSize: "13px" }} />
                                        Photo required
                                      </span>
                                    )}
                                  {subtask?.type !== "heading" &&
                                    subtask?.is_optional && (
                                      <span className="inline-flex items-center text-[11px] font-semibold text-slate-400 border border-dashed border-slate-300 rounded-full px-2 py-0.5 flex-none">
                                        Optional
                                      </span>
                                    )}
                                  <div className="flex items-center gap-1.5 flex-none">
                                    <button
                                      className="w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-violet-400 hover:text-violet-700 flex items-center justify-center transition-colors"
                                      onClick={() => handleEditSubtask(index)}
                                    >
                                      <EditOutlinedIcon style={{ fontSize: "15px" }} />
                                    </button>
                                    <button
                                      className="w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-rose-300 hover:text-rose-600 flex items-center justify-center transition-colors"
                                      onClick={() => handleDeleteSubtask(index)}
                                    >
                                      <DeleteOutlinedIcon style={{ fontSize: "15px" }} />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ) : (
                  <div className="mt-3 border border-slate-200 rounded-xl bg-white p-8 text-center">
                    <p className="text-sm font-semibold text-slate-700">
                      No subtasks yet
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Add the checklist items employees will complete when they
                      do this task.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-b border-slate-200 my-8"></div>

              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5">Schedule</h3>
              <div>
                <p className="text-sm text-slate-500">Cadence</p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    value={cadence}
                    onChange={handleCadenceChange}
                    size="small"
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      padding: "5px",
                      height: "40px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    {CADENCES.filter((c) =>
                      // A routine cannot become a one-off (or back) once it has
                      // history; the cadence within each family can change.
                      props.modalEdit ? (c.key === "one_off") === !isRoutine : true
                    ).map((c) => (
                      <MenuItem key={c.key} value={c.key}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                {cadence === "one_off" ? (
                  <div className="mt-3">
                    <p className="text-sm text-slate-500">Due</p>
                    <div className="mt-1">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          value={zonedPicker(props.modalData?.due_at, props.storeTz)}
                          onChange={handleDueChange}
                          slotProps={{
                            day: {
                              sx: {
                                "&.MuiPickersDay-root.Mui-selected": {
                                  backgroundColor: "#6d28d9",
                                },
                              },
                            },
                            // The hour / minute / AM-PM columns and the OK button
                            // default to MUI blue; keep the whole picker violet.
                            digitalClockSectionItem: {
                              sx: {
                                "&.Mui-selected": { backgroundColor: "#6d28d9", color: "white" },
                                "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#5b21b6" },
                              },
                            },
                            actionBar: {
                              sx: { "& .MuiButton-root": { color: "#6d28d9" } },
                            },
                          }}
                          sx={{
                            minWidth: "300px",
                            "& .MuiOutlinedInput-root": {
                              height: "40px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              fontSize: "14px",
                              "& fieldset": {
                                borderRadius: "8px",
                                paddingX: "25px",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#6d28d9",
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <p className="text-sm text-slate-500 mt-3">Available from (optional)</p>
                    <div className="mt-1">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          value={props.modalData?.one_off_date ? dayjs(props.modalData.one_off_date) : null}
                          onChange={handleAvailableFromChange}
                          slotProps={{
                            day: {
                              sx: {
                                "&.MuiPickersDay-root.Mui-selected": {
                                  backgroundColor: "#6d28d9",
                                },
                              },
                            },
                          }}
                          sx={{
                            minWidth: "300px",
                            "& .MuiOutlinedInput-root": {
                              height: "40px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              fontSize: "14px",
                              "& fieldset": {
                                borderRadius: "8px",
                                paddingX: "25px",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#6d28d9",
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      The task shows in Now from the available date (the due date when left blank) until it is done.
                    </p>
                  </div>
                ) : cadence === "monthly" ? (
                  <p className="text-xs text-slate-500 mt-3">
                    Any time during the month: shows in Now from the 1st and is due at the end of the last day.
                  </p>
                ) : cadence === "weekly" ? (
                  <p className="text-xs text-slate-500 mt-3">
                    Any time during the week: shows in Now from the first day of the business week and is due at the end of its last day. For specific days, use Daily and pick the days.
                  </p>
                ) : (
                  <div className="mt-3">
                    <p className="text-sm text-slate-500">Runs on</p>
                    <DayPillPicker
                      days={scheduleDays}
                      onChange={(days) =>
                        props.setModalData((prevData) => ({
                          ...prevData,
                          days_of_week: days,
                        }))
                      }
                    />
                  </div>
                )}
                {scheduleError && (
                  <p ref={scheduleErrorRef} className="text-xs text-rose-600 mt-2">{scheduleError}</p>
                )}

              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-900">Requires validation</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Submitted work waits for a manager to validate it (or return it with a note) before it counts as done.
                    </p>
                  </div>
                  <div>
                    <PurpleSwitch
                      checked={!!props.modalData?.requires_validation}
                      onChange={() =>
                        props.setModalData((prevData) => ({
                          ...prevData,
                          requires_validation: !prevData?.requires_validation,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-900">Scored audit</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Every Pass / Fail item counts toward a score, graded against the bands below when the audit is submitted. Fails can be turned into follow-up tasks and tickets.
                    </p>
                  </div>
                  <div>
                    <PurpleSwitch
                      checked={!!props.modalData?.scoring_enabled}
                      onChange={() =>
                        props.setModalData((prevData) => ({
                          ...prevData,
                          scoring_enabled: !prevData?.scoring_enabled,
                          grade_bands:
                            !prevData?.scoring_enabled && !(prevData?.grade_bands || []).length
                              ? DEFAULT_GRADE_BANDS.map((b) => ({ ...b }))
                              : prevData?.grade_bands,
                          // An audit is conducted by a chosen audience; HQ is
                          // the default until someone picks otherwise.
                          audience_permission_types:
                            !prevData?.scoring_enabled &&
                            !(prevData?.audience_permission_types || []).length &&
                            !(prevData?.auto_assignable_roles || []).length
                              ? ["company_admin"]
                              : prevData?.audience_permission_types || [],
                        }))
                      }
                    />
                  </div>
                </div>
                {!!props.modalData?.scoring_enabled && (
                  <GradeBandEditor
                    bands={props.modalData?.grade_bands || []}
                    onChange={(bands) =>
                      props.setModalData((prevData) => ({ ...prevData, grade_bands: bands }))
                    }
                  />
                )}
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-900">Track time on task</p>
                    <p className="text-xs text-slate-500 mt-1">
                      The checklist opens with Start, offers Pause, and this task shows in the time on task report.
                    </p>
                  </div>
                  <div>
                    <PurpleSwitch
                      checked={!!props.modalData?.track_time}
                      onChange={() =>
                        props.setModalData((prevData) => ({
                          ...prevData,
                          track_time: !prevData?.track_time,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              {isRoutine && (
              <div className="mt-8">
                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-900">Time window</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Available from a start time and due by an end time. Without one, the task is available all day and due at the end of the business day.
                      </p>
                    </div>
                    <div>
                      <PurpleSwitch
                        checked={timeSelectionChecked}
                        onChange={(event) => {
                          setTimeSelectionChecked((prevState) => {
                            const newState = !prevState;

                            // If we're turning off the time selection, set both bounds to null
                            if (!newState) {
                              // Update the modalData with null values for both time bounds
                              props.setModalData((prevModalData) => ({
                                ...prevModalData,
                                lower_bound_qhnum: null,
                                upper_bound_qhnum: null
                              }));
                            }

                            return newState;
                          });
                        }}
                      />
                    </div>
                  </div>
                  {timeSelectionChecked && (
                    <div>
                      <div className="mt-3 flex items-center space-x-3">
                        <div>
                          <p className="text-sm text-slate-500">Available from</p>
                          <div className="mt-1">
                            <Select
                              fullWidth
                              value={props.modalData?.lower_bound_qhnum}
                              name="start_time"
                              onChange={handleTimeChange}
                              size="small"
                              sx={{
                                borderRadius: "8px",
                                paddingY: "1px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                                height: "40px",
                                backgroundColor: "white",
                                minWidth: "150px",
                              }}
                            >
                              {generateTimeOptionsDF(0)?.map((v) => (
                                <MenuItem key={v.value} value={v.value}>
                                  {v.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <div className="text-slate-500 mt-5">
                          <ArrowForwardIcon />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Due by</p>
                          <div className="mt-1">
                            <Select
                              fullWidth
                              value={props.modalData?.upper_bound_qhnum}
                              name="end_time"
                              onChange={handleTimeChange}
                              size="small"
                              sx={{
                                borderRadius: "8px",
                                paddingY: "1px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                                height: "40px",
                                backgroundColor: "white",
                                minWidth: "150px",
                              }}
                            >
                              {generateTimeOptionsDF(0)?.map((v) => (
                                <MenuItem key={v.value} value={v.value}>
                                  {v.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}

              {cadence === "daily" && (
                <div className="mt-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-900">Repeat within day</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Should this task repeat at a regular interval throughout the day?
                      </p>
                    </div>
                    <div>
                      <PurpleSwitch
                        checked={intradayChecked}
                        onChange={() => {
                          setIntradayChecked((prev) => {
                            const newState = !prev;
                            if (!newState) {
                              props.setModalData((prevData) => ({
                                ...prevData,
                                intraday_repeat_interval_minutes: null,
                              }));
                            } else {
                              // Auto-enable time selection when turning on intraday
                              if (!timeSelectionChecked) {
                                setTimeSelectionChecked(true);
                              }
                              props.setModalData((prevData) => ({
                                ...prevData,
                                intraday_repeat_interval_minutes: 60,
                              }));
                            }
                            return newState;
                          });
                        }}
                      />
                    </div>
                  </div>
                  {intradayChecked && (
                    <div className="mt-3">
                      <p className="text-sm text-slate-500">Repeat every</p>
                      <div className="mt-1">
                        <Select
                          value={props.modalData?.intraday_repeat_interval_minutes || 60}
                          onChange={(e) => {
                            props.setModalData((prevData) => ({
                              ...prevData,
                              intraday_repeat_interval_minutes: e.target.value,
                            }));
                          }}
                          size="small"
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            padding: "5px",
                            minWidth: "150px",
                            height: "40px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          }}
                        >
                          <MenuItem value={30}>30 minutes</MenuItem>
                          <MenuItem value={60}>1 hour</MenuItem>
                          <MenuItem value={120}>2 hours</MenuItem>
                          <MenuItem value={180}>3 hours</MenuItem>
                          <MenuItem value={240}>4 hours</MenuItem>
                        </Select>
                      </div>
                      {props.modalData?.intraday_repeat_interval_minutes &&
                        props.modalData?.lower_bound_qhnum != null &&
                        props.modalData?.upper_bound_qhnum != null ? (
                        <p className="text-xs text-slate-500 mt-2">
                          This will create{" "}
                          <span className="font-semibold text-slate-700">
                            {Math.floor(
                              ((props.modalData?.upper_bound_qhnum - props.modalData?.lower_bound_qhnum) /
                                (props.modalData?.intraday_repeat_interval_minutes / 60))
                            )}
                          </span>{" "}
                          occurrences per day
                        </p>
                      ) : props.modalData?.intraday_repeat_interval_minutes ? (
                        <p className="text-xs text-amber-600 mt-2">
                          Select a start and end time above to see the number of occurrences
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              )}

              <div className="border-b border-slate-200 my-8"></div>

              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5">Audience</h3>
              <div>
                <p className="text-sm text-slate-900">
                  {props.modalData?.scoring_enabled ? "Who conducts this audit?" : "Who does this task?"}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {props.modalData?.scoring_enabled
                    ? "Exactly these people can open, answer and submit it: company admins, the store's managers, specific roles, or any mix. Everyone else with access sees the result once it is in."
                    : "The task appears automatically for everyone on shift who matches. Leave it on everyone, or narrow it to specific roles."}
                </p>
                <AudiencePermissionPicker
                  selected={props.modalData?.audience_permission_types || []}
                  onChange={(picks) =>
                    props.setModalData((prevData) => ({
                      ...prevData,
                      audience_permission_types: picks,
                    }))
                  }
                />
                <AudienceRolePicker
                  roleNames={props.roleNames || []}
                  selected={props.modalData?.auto_assignable_roles || []}
                  allowAnyone={!props.modalData?.scoring_enabled && !(props.modalData?.audience_permission_types || []).length}
                  onChange={(roleIds) =>
                    props.setModalData((prevData) => ({
                      ...prevData,
                      auto_assignable_roles: roleIds,
                    }))
                  }
                />
              </div>

              <div className="mt-8">
                {!isManager && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-900">Location Specific</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Should this task only be assigned at specific locations?
                    </p>
                  </div>
                  <div>
                    <PurpleSwitch
                      checked={locationSpecificChecked}
                      onChange={handleLocationSpecificChange}
                    />
                  </div>
                </div>
                )}

                {locationSpecificChecked && scopeUnavailable && (
                  <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-sm font-semibold text-amber-900">
                      This task targets regions or districts, which could not be loaded.
                    </p>
                    <p className="text-xs text-amber-800 mt-0.5">
                      Where it runs is unchanged. Load them again to edit its locations.
                    </p>
                    <button
                      type="button"
                      onClick={() => props.reloadStoreGroups?.()}
                      className="mt-2 text-xs font-semibold text-violet-700 hover:text-violet-800"
                    >
                      Try again
                    </button>
                  </div>
                )}
                {locationSpecificChecked && !scopeUnavailable && (
                  <div className="mt-5">
                    <p className="text-sm text-slate-500">
                      {scopeGroups.length && !isManager
                        ? "Select regions, districts, or locations. A region or district includes every location in it, now and later."
                        : "Select locations"}
                    </p>
                    <div className="mt-1">
                      <StoreScopeTree
                        groups={scopeGroups}
                        stores={scopeStores}
                        selectedGroupUuids={props.modalData?.location_specific_store_groups || []}
                        selectedStoreIds={props.modalData?.location_specific_stores || []}
                        excludedStoreIds={excludedStoreIds}
                        onChange={handleScopeChange}
                        allowGroups={!isManager}
                        selectableStoreIds={isManager && props.managedStoreIds ? Array.from(props.managedStoreIds) : null}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <SubtaskDrawer
          open={open}
          onClose={handleClose}
          subtaskData={subtaskData}
          setSubtaskData={setSubtaskData}
          subtaskEdit={subtaskEdit}
          default_modal_data={default_subtask_data}
          onSave={handleSaveSubtask}
        />
        <AiAssetGeneratorModal
          variant="task"
          open={aiGenOpen}
          handleClose={() => setAiGenOpen(false)}
          homeCompany={props.homeCompany}
          onGenerated={applyAiDraft}
        />
      </DragDropContext>
    </>
  );
}

// ---------------------------------------------------------------------------
// New-model schedule + audience pickers
// ---------------------------------------------------------------------------

const DOW_FORM_LABELS = [
  ["Mon", 0],
  ["Tue", 1],
  ["Wed", 2],
  ["Thu", 3],
  ["Fri", 4],
  ["Sat", 5],
  ["Sun", 6],
];

function DayPillPicker({ days, onChange }) {
  const all = days.length === 7;
  const toggle = (d) => {
    if (days.includes(d)) {
      if (days.length === 1) return; // a daily task needs at least one day
      onChange(days.filter((x) => x !== d).sort((a, b) => a - b));
    } else {
      onChange([...days, d].sort((a, b) => a - b));
    }
  };
  return (
    <div className="mt-2 flex items-center gap-3 flex-wrap">
      <div className="flex gap-1.5">
        {DOW_FORM_LABELS.map(([label, d]) => (
          <button
            key={d}
            type="button"
            onClick={() => toggle(d)}
            className={`w-11 h-9 rounded-lg text-xs font-bold transition-colors ${
              days.includes(d)
                ? "bg-violet-700 text-white"
                : "bg-slate-100 text-slate-400 hover:bg-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([0, 1, 2, 3, 4, 5, 6])}
        className={`text-xs font-semibold rounded-lg px-3 py-2 ${
          all
            ? "text-violet-700 bg-violet-50"
            : "text-slate-500 border border-slate-200 hover:border-slate-400"
        }`}
      >
        Every day
      </button>
      <span className="text-xs text-slate-400">
        {all
          ? "Runs every day"
          : `Runs ${days.length} day${days.length !== 1 ? "s" : ""} a week`}
      </span>
    </div>
  );
}

// The permission-based audience picks (company admins, store managers), as
// toggle chips above the roles.
function AudiencePermissionPicker({ selected, onChange }) {
  const toggle = (key) =>
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  return (
    <div className="mt-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
        By permission
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {AUDIENCE_PERMISSION_OPTIONS.map((o) => {
          const on = selected.includes(o.key);
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => toggle(o.key)}
              title={o.hint}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 ${
                on
                  ? "bg-violet-700 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-violet-400"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// allowAnyone: whether "Anyone on shift" is a valid choice (not for a scored
// audit, and not while a permission pick already narrows the audience).
function AudienceRolePicker({ roleNames, selected, onChange, allowAnyone = true }) {
  const anyone = selected.length === 0 && allowAnyone;

  // get_role_names returns one row per station, so the same role can repeat.
  const uniqueRoles = [];
  const seen = new Set();
  (roleNames || []).forEach((r) => {
    if (seen.has(r.id)) return;
    seen.add(r.id);
    uniqueRoles.push(r);
  });

  const groups = {};
  uniqueRoles.forEach((r) => {
    const g = r.role_type || "Roles";
    if (!groups[g]) groups[g] = [];
    groups[g].push(r);
  });

  const toggle = (id) =>
    onChange(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id]
    );

  return (
    <div className="mt-3">
      {allowAnyone && (
        <button
          type="button"
          onClick={() => onChange([])}
          className={`text-xs font-bold rounded-full px-3.5 py-1.5 ${
            anyone
              ? "bg-violet-700 text-white"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          Anyone on shift
        </button>
      )}
      {Object.entries(groups).map(([groupName, roles]) => (
        <div key={groupName} className="mt-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
            {groupName}
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {roles.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => toggle(r.id)}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 ${
                  selected.includes(r.id)
                    ? "bg-violet-700 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-violet-400"
                }`}
              >
                {r.role_name}
              </button>
            ))}
          </div>
        </div>
      ))}
      {!anyone && selected.length > 0 && (
        <p className="text-[11px] text-slate-400 mt-2">
          Only people working these roles will see this task.
        </p>
      )}

    </div>
  );
}

export default DailyPlanFormBuilder;
